// VendorProductList.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  IconButton,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  Stack,
  useMediaQuery,
  Switch,
  FormControlLabel,
  Checkbox,
  Chip,
  InputAdornment,
  Avatar,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Tooltip from "@mui/material/Tooltip";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InventoryIcon from "@mui/icons-material/Inventory";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBack from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import api from "../../api/axios";
import LoadingScreen from "../../components/LoadingScreen";

/* ── Status config ── */
const STATUS_CONFIG = {
  published: { color: '#2e7d32', bg: '#e8f5e9', label: 'Published' },
  unlisted:  { color: '#f57c00', bg: '#fff3e0', label: 'Unlisted' },
  draft:     { color: '#757575', bg: '#f5f5f5', label: 'Draft' },
  pending_review: { color: '#b26a00', bg: '#fff8e1', label: 'Pending review' },
};

function StatusDot({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <FiberManualRecordIcon sx={{ fontSize: 10, color: cfg.color }} />
      <Typography variant="caption" fontWeight={600} sx={{ color: cfg.color, fontSize: '0.75rem' }}>
        {cfg.label}
      </Typography>
    </Box>
  );
}

function MobileProductCard({ product, onEdit, onDelete, onToggleFeatured, isSelected, onSelect, editingStockId, editingStockValue, onStockClick, onStockChange, onStockSave, onStockCancel }) {
  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: 3,
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        border: isSelected ? '2px solid #ff9900' : '2px solid transparent',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        },
      }}
    >
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <Checkbox
          checked={isSelected}
          onChange={() => onSelect(product._id)}
          sx={{ p: 0, mt: 0.5 }}
        />
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Tooltip title={product.title || ''} arrow placement="top">
              <Typography fontWeight={600} sx={{ fontSize: '1rem', lineHeight: 1.3, cursor: 'pointer', maxWidth: '80%' }}>
                {product.title && product.title.length > 75 ? product.title.substring(0, 75) + '...' : product.title}
              </Typography>
            </Tooltip>
            <StatusDot status={product.status} />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {product.category?.name || 'Uncategorized'}
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Box>
              <Typography fontWeight={700} sx={{ fontSize: '1.1rem', color: '#ff9900' }}>
                ₹{Number(product.price || 0).toLocaleString("en-IN")}
              </Typography>
            </Box>
            <Box>
              {editingStockId === product._id ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    type="number"
                    value={editingStockValue}
                    onChange={(e) => onStockChange(e.target.value)}
                    sx={{ width: 80 }}
                    autoFocus
                    inputProps={{ min: 0, style: { textAlign: 'center', fontSize: '0.9rem' } }}
                  />
                  <IconButton size="small" sx={{ color: '#2e7d32' }} onClick={onStockSave}><CheckIcon fontSize="small" /></IconButton>
                  <IconButton size="small" sx={{ color: '#d32f2f' }} onClick={onStockCancel}><CloseIcon fontSize="small" /></IconButton>
                </Stack>
              ) : (
                <Stack direction="row" spacing={0.5} alignItems="center" onClick={() => onStockClick(product._id, product.stock)} sx={{ cursor: 'pointer' }}>
                  <Typography variant="body2" color="text.secondary">Stock:</Typography>
                  <Typography
                    fontWeight={600}
                    sx={{
                      color: (product.stock || 0) === 0 ? '#d32f2f' :
                             (product.stock || 0) <= 10 ? '#f57c00' : '#2e7d32',
                      borderBottom: '1px dashed #ccc',
                      lineHeight: 1.2,
                    }}
                  >
                    {product.stock ?? "-"}
                  </Typography>
                  {(product.stock || 0) <= 10 && (product.stock || 0) > 0 && (
                    <WarningIcon sx={{ color: "#f57c00", fontSize: 16 }} />
                  )}
                  {(product.stock || 0) === 0 && (
                    <ErrorIcon sx={{ color: "#d32f2f", fontSize: 16 }} />
                  )}
                </Stack>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={product.isFeatured || false}
                  onChange={() => onToggleFeatured && onToggleFeatured(product._id, !product.isFeatured)}
                  size="small"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#ff9900',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#ff9900',
                    },
                  }}
                />
              }
              label="Featured"
              labelPlacement="end"
              sx={{ m: 0, '& .MuiTypography-root': { fontSize: '0.8rem' } }}
            />

            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={onEdit}
                  sx={{ color: '#ff9900', bgcolor: 'rgba(255,153,0,0.1)', '&:hover': { bgcolor: 'rgba(255,153,0,0.2)' } }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={
                  product.status === "published"
                    ? "Unlist"
                    : product.status === "pending_review"
                    ? "Awaiting manual review"
                    : "Publish"
                }
              >
                <IconButton
                  size="small"
                  disabled={product.status === "pending_review"}
                  onClick={async () => {
                    await api.patch("/products/" + product._id + "/toggle-visibility");
                    window.location.reload();
                  }}
                  sx={{
                    color: product.status === "published" ? "#f57c00" : "#2e7d32",
                    bgcolor: product.status === "published" ? 'rgba(245,124,0,0.1)' : 'rgba(46,125,50,0.1)',
                    '&:hover': {
                      bgcolor: product.status === "published" ? 'rgba(245,124,0,0.2)' : 'rgba(46,125,50,0.2)',
                    }
                  }}
                >
                  {product.status === "published" ? <VisibilityOffIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={onDelete}
                  sx={{ color: '#d32f2f', bgcolor: 'rgba(211,47,47,0.1)', '&:hover': { bgcolor: 'rgba(211,47,47,0.2)' } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}

/* ── Helper: extract first image thumbnail URL from product ── */
const getThumbnail = (product) => {
  // media array: find first image entry
  if (product.media?.length) {
    for (const m of product.media) {
      if (m.type === 'image' || m.mimeType?.startsWith('image/')) {
        return m.thumbnailUrl || m.url || '';
      }
    }
    // If no image found, fallback to first media item's thumbnail
    return product.media[0]?.thumbnailUrl || product.media[0]?.url || '';
  }
  // images array
  if (product.images?.length) {
    const img = product.images[0];
    if (typeof img === 'string') return img;
    return img.thumbnailUrl || img.url || '';
  }
  return '';
};

export default function VendorProductList() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  // Table header filters
  const [stockFilter, setStockFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);

  // Delete dialog
  const [deleteId, setDeleteId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Inventory management
  const [editingStockId, setEditingStockId] = useState(null);
  const [editingStockValue, setEditingStockValue] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [bulkStockValue, setBulkStockValue] = useState("");
  const [inventorySummary, setInventorySummary] = useState({
    totalProducts: 0,
    totalStock: 0,
    lowStockCount: 0,
    outOfStockCount: 0,
  });

  const [searchParams] = useSearchParams();
  const initialType = searchParams.get("type") || "all";
  const [archivedCount, setArchivedCount] = useState(0);

  const filterParam = searchParams.get("filter");


  // Apply filter from URL parameter
  useEffect(() => {
    if (filterParam === "low+out") {
      setStockFilter("low+out");
    }
  }, [filterParam]);

  /* ======================
     FETCH VENDOR PRODUCTS
     ====================== */
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const res = await api.get("/products?type=" + initialType + "&vendor=true");
        setProducts(res.data.products || []);
      } catch (err) {
        console.error("Fetch products failed", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [initialType]);

  /* ======================
     FETCH RECYCLE BIN COUNT (best-effort; never blocks loading)
     ====================== */
  const fetchArchivedCount = React.useCallback(() => {
    api.get("/products/archived")
      .then((res) => setArchivedCount((res.data.products || []).length))
      .catch((err) => console.error("Fetch archived count failed", err));
  }, []);

  useEffect(() => {
    fetchArchivedCount();
  }, [fetchArchivedCount]);

  /* ======================
     DYNAMIC CATEGORY LIST
     ====================== */
  const CATEGORY_LIST = [
    ...new Map(
      products
        .filter((p) => p.category)
        .map((p) => [p.category._id, p.category])
    ).values(),
  ];

  /* ======================
     FILTERING
     ====================== */
  const filtered = products.filter((p) => {
    const matchesSearch = p.title?.toLowerCase().includes(search.toLowerCase());

    const matchesCategory = categoryFilter ? p.category?._id === categoryFilter : true;

    const matchesStock = (() => {
      const stock = p.stock || 0;
      switch (stockFilter) {
        case "low": return stock > 0 && stock <= 10;
        case "out": return stock === 0;
        case "sufficient": return stock > 10;
        case "low+out": return (stock > 0 && stock <= 10) || stock === 0;
        case "all":
        default: return true;
      }
    })();

    const matchesStatus = statusFilter === "all" || p.status === statusFilter;

    return matchesSearch && matchesCategory && matchesStock && matchesStatus;
  });

  const isMobile = useMediaQuery("(max-width:768px)");

  /* ======================
     PAGINATION
     ====================== */
  const paginated = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  /* ======================
     TOGGLE FEATURED STATUS
     ====================== */
  const handleToggleFeatured = async (productId, newFeaturedStatus) => {
    try {
      await api.patch("/products/" + productId, {
        isFeatured: newFeaturedStatus
      });

      setProducts((prev) =>
        prev.map((p) =>
          p._id === productId
            ? { ...p, isFeatured: newFeaturedStatus, featuredAt: newFeaturedStatus ? new Date() : null }
            : p
        )
      );
    } catch (error) {
      console.error("Failed to update featured status:", error);
    }
  };

  /* ======================
     DELETE PRODUCT
     ====================== */
  const confirmDelete = async () => {
    try {
      await api.delete("/products/" + deleteId);
      setProducts((prev) => prev.filter((p) => p._id !== deleteId));
      fetchArchivedCount(); // keep the Recycle Bin widget in sync
    } catch {
      alert("Failed to delete product");
    } finally {
      setDeleteOpen(false);
      setDeleteId(null);
    }
  };

  /* ── Inline stock edit (click directly on number) ── */
  const handleStockClick = (productId, currentStock) => {
    setEditingStockId(productId);
    setEditingStockValue(currentStock?.toString() || "");
  };

  const saveStockEdit = async () => {
    if (!editingStockId || editingStockValue === "") return;

    try {
      const stockNum = parseInt(editingStockValue, 10);
      if (isNaN(stockNum) || stockNum < 0) {
        alert("Please enter a valid non-negative number");
        return;
      }

      await api.patch("/products/" + editingStockId, { stock: stockNum });

      setProducts((prev) =>
        prev.map((p) =>
          p._id === editingStockId ? { ...p, stock: stockNum } : p
        )
      );

      setEditingStockId(null);
      setEditingStockValue("");
    } catch (error) {
      console.error("Failed to update stock:", error);
      alert("Failed to update stock");
    }
  };

  const cancelStockEdit = () => {
    setEditingStockId(null);
    setEditingStockValue("");
  };

  const toggleProductSelection = (productId) => {
    setSelectedProducts((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  const selectAllProducts = () => {
    if (selectedProducts.length === filtered.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filtered.map((p) => p._id));
    }
  };

  const handleBulkStockUpdate = async () => {
    if (!bulkStockValue || selectedProducts.length === 0) {
      alert("Please select products and enter a stock value");
      return;
    }

    const stockNum = parseInt(bulkStockValue, 10);
    if (isNaN(stockNum) || stockNum < 0) {
      alert("Please enter a valid non-negative number");
      return;
    }

    try {
      const updates = selectedProducts.map((productId) => ({
        productId,
        stock: stockNum
      }));

      await api.post("/products/bulk-stock-update", { updates });

      setProducts((prev) =>
        prev.map((p) =>
          selectedProducts.includes(p._id) ? { ...p, stock: stockNum } : p
        )
      );

      setBulkStockValue("");
      setSelectedProducts([]);
      alert(`Stock updated for ${selectedProducts.length} product(s)`);
    } catch (error) {
      console.error("Bulk stock update failed:", error);
      alert("Failed to update stock for selected products");
    }
  };

  /* ======================
     CALCULATE SUMMARY
     ====================== */
  useEffect(() => {
    const publishedProducts = products.filter(p => p.status === "published");
    const totalProducts = publishedProducts.length;
    const totalStock = publishedProducts.reduce((sum, p) => sum + (Number(p.stock) || 0), 0);
    const lowStockCount = publishedProducts.filter((p) => {
      const stock = Number(p.stock) || 0;
      return stock > 0 && stock <= 10;
    }).length;
    const outOfStockCount = publishedProducts.filter((p) => (Number(p.stock) || 0) === 0).length;

    setInventorySummary({ totalProducts, totalStock, lowStockCount, outOfStockCount });
  }, [products]);

  /* ======================
     FILTER ACTIONS
     ====================== */
  const applyLowStockFilter = () => {
    setStockFilter("low");
    setPage(0);
  };

  const applyOutOfStockFilter = () => {
    setStockFilter("out");
    setPage(0);
  };

  const resetAllFilters = () => {
    setSearch("");
    setCategoryFilter("");
    setStockFilter("all");
    setStatusFilter("all");
    setPage(0);
  };

  const isAnyFilterActive = () => {
    return (
      search !== "" ||
      categoryFilter !== "" ||
      stockFilter !== "all" ||
      statusFilter !== "all"
    );
  };

  // Card accent palette (matches Dashboard)
  const CARD_ACCENTS = {
    totalProducts: { icon: '#1976d2', bg: 'rgba(25,118,210,0.10)', border: '#e3eeff' },
    lowStock:      { icon: '#ed6c02', bg: 'rgba(237,108,2,0.10)',  border: '#ffe0b2' },
    outOfStock:    { icon: '#d32f2f', bg: 'rgba(211,47,47,0.10)',  border: '#ffcdd2' },
    recycleBin:    { icon: '#546e7a', bg: 'rgba(84,110,122,0.10)', border: '#cfd8dc' },
  };

  // Stat card style (matches Dashboard)
  const StatCard = ({ icon: Icon, value, label, accentKey, onClick, hint }) => {
    const accent = CARD_ACCENTS[accentKey] || CARD_ACCENTS.totalProducts;
    return (
      <Box
        onClick={onClick}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={(e) => {
          if (onClick && (e.key === 'Enter' || e.key === ' ')) onClick();
        }}
        sx={{
          p: 1.25,
          borderRadius: '12px',
          bgcolor: '#fff',
          border: `1.5px solid ${accent.border}`,
          boxShadow: '0 2px 10px rgba(15,23,42,0.06)',
          cursor: onClick ? 'pointer' : 'default',
          transition: 'transform 0.18s ease, box-shadow 0.18s ease',
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 1.25 },
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
          boxSizing: 'border-box',
          minHeight: { xs: 80, sm: 80, md: 90 },
          '&:hover': onClick
            ? { transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(15,23,42,0.10)' }
            : {},
        }}
      >
        <Box
          sx={{
            width: { xs: 34, sm: 38, lg: 36 },
            height: { xs: 34, sm: 38, lg: 36 },
            borderRadius: '10px',
            bgcolor: accent.bg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: { xs: 18, sm: 20, lg: 18 }, color: accent.icon }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontSize: { xs: '0.65rem', sm: '0.7rem', lg: '0.68rem' },
              fontWeight: 500,
              color: '#64748b',
              lineHeight: 1.2,
              mb: 0.4,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '1.1rem', sm: '1.2rem', lg: '1rem' },
              fontWeight: 700,
              lineHeight: 1,
              color: '#0f172a',
            }}
          >
            {value}
          </Typography>
        </Box>
        {hint && (
          <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
            {hint}
          </Typography>
        )}
      </Box>
    );
  };

  /* ── Filter tab config ── */
  const FILTER_TABS = [
    { key: 'all', label: 'All', type: 'status' },
    { key: 'low', label: 'Low Stock', type: 'stock' },
    { key: 'out', label: 'Out of Stock', type: 'stock' },
    { key: 'pending_review', label: 'Pending review', type: 'status' },
    { key: 'published', label: 'Published', type: 'status' },
    { key: 'unlisted', label: 'Unlisted', type: 'status' },
    { key: 'draft', label: 'Draft', type: 'status' },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f2f5' }}>
      {/* ================= HEADER (matches Manage Orders style) ================= */}
      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #0062ff 100%)', px: { xs: 2, sm: 4 }, py: 2.5, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 0.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Button onClick={() => navigate("/vendor/dashboard")} sx={{ color: "rgba(255,255,255,0.7)", textTransform: "none", fontSize: "0.82rem", fontWeight: 500, mb: 0.5, p: 0, "&:hover": { color: "#fff", bgcolor: "transparent" }, display: "flex", alignItems: "center", gap: 0.5 }}>
              <ArrowBack sx={{ fontSize: 16 }} />
              Back to Dashboard
            </Button>
            <Typography variant="h5" fontWeight={700} sx={{ color: '#fff' }}>📦 Inventory</Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/vendor/products/new")}
            sx={{
              bgcolor: '#ff9900',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1.2,
              boxShadow: '0 4px 14px rgba(255,153,0,0.3)',
              '&:hover': {
                bgcolor: '#e68a00',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(255,153,0,0.4)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            Add Product
          </Button>
        </Box>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>Manage your product catalog.</Typography>
      </Box>

      {loading ? (
        <Box sx={{ px: { xs: 2, sm: 4 }, pb: 4 }}>
          <LoadingScreen message="Loading your inventory..." />
        </Box>
      ) : (
      <Box sx={{ px: { xs: 2, sm: 4 }, pb: 4 }}>

      {/* Inventory Summary Cards (Total Stock removed — vanity metric) */}
      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          alignItems: 'stretch',
          mb: 1.5,
        }}
      >
        <StatCard
          icon={InventoryIcon}
          value={inventorySummary.totalProducts}
          label="Active Products"
          accentKey="totalProducts"
          //hint="Published items"
        />
        <StatCard
          icon={WarningIcon}
          value={inventorySummary.lowStockCount}
          label="Low Stock"
          accentKey="lowStock"
        />
        <StatCard
          icon={ErrorIcon}
          value={inventorySummary.outOfStockCount}
          label="Out of Stock"
          accentKey="outOfStock"
        />
        <StatCard
          icon={RestoreFromTrashIcon}
          value={archivedCount}
          label="Recycle Bin"
          accentKey="recycleBin"
          onClick={() => navigate("/vendor/products/archived")}
        />
      </Box>

      {/* ── Filter Tabs (Status + Stock) ── */}
      <Box sx={{ mb: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {FILTER_TABS.map((tab) => {
          const active = tab.type === 'status'
            ? statusFilter === tab.key
            : stockFilter === tab.key;
          const cfg = STATUS_CONFIG[tab.key];
          return (
            <Button
              key={tab.key}
              size="small"
              onClick={() => {
                if (tab.key === 'all') {
                  setStockFilter('all');
                  setStatusFilter('all');
                } else if (tab.type === 'status') {
                  setStatusFilter(tab.key);
                  setStockFilter('all');
                } else {
                  setStockFilter(tab.key);
                  setStatusFilter('published');
                }
                setPage(0);
              }}
              sx={{
                textTransform: 'none',
                fontWeight: active ? 700 : 500,
                borderRadius: '20px',
                px: 2,
                py: 0.6,
                fontSize: '0.8rem',
                bgcolor: active
                  ? (cfg ? cfg.bg : '#e3f2fd')
                  : 'transparent',
                color: active
                  ? (cfg ? cfg.color : '#1976d2')
                  : '#64748b',
                border: active
                  ? `1.5px solid ${cfg ? cfg.color : '#1976d2'}`
                  : '1.5px solid transparent',
                '&:hover': {
                  bgcolor: active
                    ? (cfg ? cfg.bg : '#e3f2fd')
                    : 'rgba(0,0,0,0.04)',
                },
              }}
            >
              {tab.label}
              {tab.key !== 'all' && (
                <Chip
                  label={tab.type === 'status'
                    ? products.filter(p => p.status === tab.key).length
                    : products.filter(p => p.status === 'published' && (
                        (tab.key === 'low' ? ((p.stock || 0) > 0 && (p.stock || 0) <= 10) :
                         tab.key === 'out' ? ((p.stock || 0) === 0) :
                         (p.stock || 0) > 10)
                      )).length
                  }
                  size="small"
                  sx={{
                    ml: 0.75,
                    height: 18,
                    minWidth: 18,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    bgcolor: active ? (cfg ? cfg.bg : '#e3f2fd') : 'rgba(0,0,0,0.06)',
                    color: active ? (cfg ? cfg.color : '#1976d2') : '#64748b',
                  }}
                />
              )}
            </Button>
          );
        })}
      </Box>

      {/* Search & Filter Bar */}
      <Paper sx={{ mb: 1.5, p: 2, borderRadius: '12px', boxShadow: '0 2px 10px rgba(15,23,42,0.06)', border: '1.5px solid #f1f5f9' }}>
        <Stack spacing={2}>
          {/* Main Search Row */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ flex: 1 }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#999' }} />
                  </InputAdornment>
                ),
              }}
            />
            <Select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              displayEmpty
              size="small"
              sx={{ minWidth: { xs: '100%', sm: 180 } }}
            >
              <MenuItem value="">All Categories</MenuItem>
              {CATEGORY_LIST.map((cat) => (
                <MenuItem key={cat._id} value={cat._id}>{cat.name}</MenuItem>
              ))}
            </Select>
            {isAnyFilterActive() && (
              <Button
                variant="text"
                startIcon={<RefreshIcon />}
                onClick={resetAllFilters}
                sx={{ color: '#666' }}
              >
                Reset
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* Selection Bar */}
      {selectedProducts.length > 0 && (
        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'rgba(255,153,0,0.1)', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" fontWeight={500}>
            {selectedProducts.length} of {filtered.length} selected
          </Typography>
          <Button size="small" variant="text" onClick={selectAllProducts} sx={{ color: '#ff9900' }}>
            {selectedProducts.length === filtered.length ? 'Deselect All' : 'Select All'}
          </Button>
        </Box>
      )}

      {/* Products Grid / Table */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 2px 10px rgba(15,23,42,0.06)', border: '1.5px solid #f1f5f9', p: 0 }}>
        {isMobile ? (
          // Mobile: Card View
          <Box sx={{ p: 2 }}>
            <Stack spacing={2}>
              {paginated.map((p) => (
                <MobileProductCard
                  key={p._id}
                  product={p}
                  onEdit={() => navigate("/vendor/edit-product/" + p._id)}
                  onDelete={() => { setDeleteId(p._id); setDeleteOpen(true); }}
                  onToggleFeatured={handleToggleFeatured}
                  isSelected={selectedProducts.includes(p._id)}
                  onSelect={toggleProductSelection}
                  editingStockId={editingStockId}
                  editingStockValue={editingStockValue}
                  onStockClick={handleStockClick}
                  onStockChange={(val) => setEditingStockValue(val)}
                  onStockSave={saveStockEdit}
                  onStockCancel={cancelStockEdit}
                />
              ))}
              {filtered.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <InventoryIcon sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">No products found</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {search || categoryFilter || stockFilter !== 'all' || statusFilter !== 'all'
                      ? 'Try adjusting your filters'
                      : 'Add your first product to get started'}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>
        ) : (
          // Desktop: Modern Table
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: 950 }}>
              {/* Table Header (sticky) */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 1.5,
                bgcolor: '#fafafa',
                borderBottom: '1px solid #eee',
                fontWeight: 600,
                position: 'sticky',
                top: 0,
                zIndex: 10,
              }}>
                <Box sx={{ width: 40, flexShrink: 0 }}>
                  <Checkbox
                    indeterminate={selectedProducts.length > 0 && selectedProducts.length < filtered.length}
                    checked={filtered.length > 0 && selectedProducts.length === filtered.length}
                    onChange={selectAllProducts}
                    size="small"
                  />
                </Box>
                <Box sx={{ flex: 3.5, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1, pr: 2 }}>
                  <Typography fontWeight={600} sx={{ fontSize: '0.85rem' }}>Product</Typography>
                </Box>
                <Box sx={{ flex: 1.2, minWidth: 80 }}><Typography fontWeight={600} sx={{ fontSize: '0.85rem' }}>Category</Typography></Box>
                <Box sx={{ flex: 0.8, minWidth: 60, textAlign: 'center' }}><Typography fontWeight={600} sx={{ fontSize: '0.85rem' }}>Featured</Typography></Box>
                <Box sx={{ flex: 0.8, minWidth: 60, textAlign: 'right', pr: 5 }}><Typography fontWeight={600} sx={{ fontSize: '0.85rem' }}>Price</Typography></Box>
                <Box sx={{ flex: 2, minWidth: 180, textAlign: 'center' }}><Typography fontWeight={600} sx={{ fontSize: '0.85rem' }}>Stock</Typography></Box>
                <Box sx={{ flex: 1, minWidth: 80, textAlign: 'center' }}><Typography fontWeight={600} sx={{ fontSize: '0.85rem' }}>Status</Typography></Box>
                <Box sx={{ flex: 0.8, minWidth: 80, textAlign: 'right' }}><Typography fontWeight={600} sx={{ fontSize: '0.85rem' }}>Actions</Typography></Box>
              </Box>

              {/* Table Body */}
              {paginated.map((p) => {
                return (
                  <Box
                    key={p._id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 2,
                      py: 2,
                      borderBottom: '1px solid #f5f5f5',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'rgba(255,153,0,0.04)' },
                    }}
                    onClick={() => navigate("/vendor/edit-product/" + p._id)}
                  >
                    <Box sx={{ width: 32, flexShrink: 0 }}>
                      <Checkbox
                        checked={selectedProducts.includes(p._id)}
                        onChange={() => toggleProductSelection(p._id)}
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Box>
                    <Box sx={{ flex: 3.5, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.5, overflow: 'hidden', pr: 2 }}>
                      {/* Thumbnail */}
                      <Avatar
                        src={getThumbnail(p)}
                        alt={p.title || ''}
                        variant="rounded"
                        sx={{ width: 40, height: 40, bgcolor: '#f0f0f0', flexShrink: 0 }}
                      >
                        {p.title?.[0]?.toUpperCase() || 'P'}
                      </Avatar>
                      <Tooltip title={p.title || ''} arrow placement="top">
                        <Typography fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                          {p.title && p.title.length > 75 ? p.title.substring(0, 75) + '...' : p.title}
                        </Typography>
                      </Tooltip>
                    </Box>
                    <Box sx={{ flex: 1.2, minWidth: 80, overflow: 'hidden' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.category?.name || '-'}</Typography>
                    </Box>
                    <Box sx={{ flex: 0.8, minWidth: 60, textAlign: 'center' }}>
                      <Switch
                        checked={p.isFeatured || false}
                        onChange={() => handleToggleFeatured(p._id, !p.isFeatured)}
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#ff9900' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#ff9900' },
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 0.8, minWidth: 60, textAlign: 'right', pr: 5 }}>
                          <Typography fontWeight={600} sx={{ color: '#ff9900', fontSize: '0.85rem' }}>₹{Number(p.price || 0).toLocaleString("en-IN")}</Typography>
                    </Box>
                    <Box sx={{ flex: 2, minWidth: 180, textAlign: 'center' }}>
                      {editingStockId === p._id ? (
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" onClick={(e) => e.stopPropagation()}>
                          <TextField
                            size="small"
                            type="number"
                            value={editingStockValue}
                            onChange={(e) => setEditingStockValue(e.target.value)}
                            sx={{ width: 140 }}
                            autoFocus
                            inputProps={{ min: 0, style: { textAlign: 'center', fontSize: '0.85rem' } }}
                          />
                          <IconButton size="small" sx={{ color: '#2e7d32' }} onClick={saveStockEdit}><CheckIcon fontSize="small" /></IconButton>
                          <IconButton size="small" sx={{ color: '#d32f2f' }} onClick={cancelStockEdit}><CloseIcon fontSize="small" /></IconButton>
                        </Stack>
                      ) : (
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center', cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStockClick(p._id, p.stock);
                          }}
                        >
                          <Typography
                            fontWeight={600}
                            sx={{
                              color: (p.stock || 0) === 0 ? '#d32f2f' : (p.stock || 0) <= 10 ? '#f57c00' : '#333',
                              textAlign: 'center',
                              borderBottom: '1px dashed #ccc',
                              lineHeight: 1.2,
                              fontSize: '0.85rem',
                            }}
                          >
                            {p.stock ?? "-"}
                          </Typography>
                          {(p.stock || 0) <= 10 && (p.stock || 0) > 0 && <WarningIcon sx={{ color: "#f57c00", fontSize: 16 }} />}
                          {(p.stock || 0) === 0 && <ErrorIcon sx={{ color: "#d32f2f", fontSize: 16 }} />}
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 80, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <StatusDot status={p.status} />
                    </Box>
                    <Box sx={{ flex: 0.8, minWidth: 80, display: 'flex', justifyContent: 'flex-end', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => navigate("/vendor/edit-product/" + p._id)}
                          sx={{ color: '#ff9900', '&:hover': { bgcolor: 'rgba(255,153,0,0.1)' } }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          p.status === "published"
                            ? "Unlist"
                            : p.status === "pending_review"
                            ? "Awaiting manual review"
                            : "Publish"
                        }
                      >
                        <IconButton
                          size="small"
                          disabled={p.status === "pending_review"}
                          onClick={async () => {
                            await api.patch("/products/" + p._id + "/toggle-visibility");
                            setProducts((prev) =>
                              prev.map((prod) =>
                                prod._id === p._id
                                  ? { ...prod, status: prod.status === "published" ? "unlisted" : "published" }
                                  : prod
                              )
                            );
                          }}
                          sx={{
                            color: p.status === "published" ? "#f57c00" : "#2e7d32",
                            '&:hover': {
                              bgcolor: p.status === "published" ? 'rgba(245,124,0,0.1)' : 'rgba(46,125,50,0.1)',
                            }
                          }}
                        >
                          {p.status === "published" ? <VisibilityOffIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => { setDeleteId(p._id); setDeleteOpen(true); }}
                          sx={{ color: '#d32f2f', '&:hover': { bgcolor: 'rgba(211,47,47,0.1)' } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                );
              })}

              {filtered.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <InventoryIcon sx={{ fontSize: 64, color: '#ddd', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">No products found</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {search || categoryFilter || stockFilter !== 'all' || statusFilter !== 'all'
                      ? 'Try adjusting your filters or search terms'
                      : 'Add your first product to get started'}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Pagination */}
        <Box sx={{ px: 2, py: 2, borderTop: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {paginated.length} of {filtered.length} products
          </Typography>
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[8, 16, 32]}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.85rem' } }}
          />
        </Box>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <Box sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              bgcolor: 'rgba(211,47,47,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <DeleteIcon sx={{ color: '#d32f2f', fontSize: 30 }} />
            </Box>
          </Box>
          <Typography variant="h6" fontWeight={600}>Delete Product?</Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone. The product will be permanently removed from your inventory.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', gap: 1 }}>
          <Button
            onClick={() => setDeleteOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2, borderColor: '#ddd', color: '#666' }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            sx={{ borderRadius: 2 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
      )}
    </Box>
  );
}
